// The page editor's 52px top bar, laid out like Flowmark's:
// left    back, page name, Live / Draft (or "Unpublished changes")
// center  preview size (Desktop / Tablet / Phone) and canvas mode (Edit / Try it),
//         exactly centered: the side zones share the rest of the width
// right   Undo, Redo, Version history, Analytics, Share, Preview, then the save
//         status, Publish and the More menu.
// Below 1280px the status pill and the tools fold into More; below 1024px a
// page switcher opens the rail drawer and Edit / Try it moves into More; at
// 640px and under only back, the switcher, the save icon, Publish and More stay.
// People who can only view get the same bar without the writing controls.
import type { ReactNode } from 'react';
import {
  ArrowLeftIcon, ChartIcon, CheckIcon, ChevronDownIcon, CircleAlertIcon, DeviceDesktopIcon, DevicePhoneIcon,
  DeviceTabletIcon, EyeIcon, EyeOffIcon, FileIcon, HistoryIcon, LayersIcon, MoreHorizontalIcon, PlayIcon,
  PointerIcon, RedoIcon, ShareIcon, UndoIcon,
} from '../icons';
import { EditorMenu, type EditorMenuEntry } from './EditorMenu';
import { SegGlide } from './SegGlide';
import type { CanvasMode, EditorDevice } from './types';

/** What the save status shows. Errors keep their own words (and a retry, or
 *  a reload when the page changed somewhere else). */
export type SaveStatus =
  | { kind: 'error'; message: string; retryable: boolean; stale?: boolean }
  | { kind: 'publishing' | 'unpublishing' | 'saving' | 'dirty' | 'formNotLive' | 'saved' };

type QuietKind = Exclude<SaveStatus['kind'], 'error'>;

const SAVE_TEXT: Record<QuietKind, string> = {
  publishing: 'Publishing…',
  unpublishing: 'Unpublishing…',
  saving: 'Saving…',
  dirty: 'Unsaved changes',
  formNotLive: 'Form not live yet',
  saved: 'Saved',
};

const spinner = <span className="pe-spinner" aria-hidden="true" />;
const SAVE_ICON: Record<QuietKind, ReactNode> = {
  publishing: spinner,
  unpublishing: spinner,
  saving: spinner,
  dirty: <span className="pe-save-dot" aria-hidden="true" />,
  formNotLive: <CircleAlertIcon size={13} />,
  saved: <CheckIcon size={13} />,
};

const DEVICES: [EditorDevice, typeof DeviceDesktopIcon, string][] = [
  ['desktop', DeviceDesktopIcon, 'Desktop'],
  ['tablet', DeviceTabletIcon, 'Tablet'],
  ['phone', DevicePhoneIcon, 'Phone'],
];

/** More menu items that repeat the toolbar: shown only below 1280px. */
const NARROW_ONLY = 'xl:hidden';

export interface EditorHeaderProps {
  name: string;
  onNameChange: (name: string) => void;
  live: boolean;
  /** A live page with edits that are not live yet. */
  unpublished: boolean;
  /** Page switcher (below 1024px): the current step's name, or "Thank you". */
  stepLabel: string;
  onOpenRail: () => void;
  device: EditorDevice;
  onDeviceChange: (d: EditorDevice) => void;
  mode: CanvasMode;
  onModeChange: (m: CanvasMode) => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onOpenHistory: () => void;
  onOpenAnalytics: () => void;
  onOpenShare: () => void;
  onPreview: () => void;
  previewBusy: boolean;
  save: SaveStatus;
  /** Clicking the status saves now (a draft with unsaved changes). Absent: not clickable. */
  onSaveNow?: () => void;
  /** The retry on a failed write. */
  onRetry: () => void;
  /** "Name the page first." / "Fix the page link first.": show the field. */
  onFixError: () => void;
  /** The page changed somewhere else: load the latest version. */
  onReload: () => void;
  /** People who can only view: no name edits, undo, publish, save or template. */
  readOnly?: boolean;
  publishLabel: string;
  publishDisabled: boolean;
  publishTitle?: string;
  onPublish: () => void;
  onSaveTemplate: () => void;
  /** Live pages only: Unpublish in the More menu. */
  onUnpublish?: () => void;
  unpublishDisabled?: boolean;
  onBack: () => void;
}

export function EditorHeader(p: EditorHeaderProps) {
  const { save, readOnly = false } = p;
  const moreItems: EditorMenuEntry[] = [
    ...(readOnly ? [] : [
      { label: 'Undo', icon: <UndoIcon size={15} />, onSelect: p.onUndo, disabled: !p.canUndo, className: NARROW_ONLY },
      { label: 'Redo', icon: <RedoIcon size={15} />, onSelect: p.onRedo, disabled: !p.canRedo, className: NARROW_ONLY },
    ]),
    { label: 'Version history', icon: <HistoryIcon size={15} />, onSelect: p.onOpenHistory, className: NARROW_ONLY },
    { label: 'Analytics', icon: <ChartIcon size={15} />, onSelect: p.onOpenAnalytics, className: NARROW_ONLY },
    { label: 'Share and embed', icon: <ShareIcon size={15} />, onSelect: p.onOpenShare, className: NARROW_ONLY },
    { label: p.previewBusy ? 'Opening preview…' : 'Preview', icon: <EyeIcon size={15} />, onSelect: p.onPreview, disabled: p.previewBusy, className: NARROW_ONLY },
    p.mode === 'edit'
      ? { label: 'Try it like a visitor', icon: <PlayIcon size={15} />, onSelect: () => p.onModeChange('try'), className: NARROW_ONLY }
      : { label: 'Back to editing', icon: <PointerIcon size={15} />, onSelect: () => p.onModeChange('edit'), className: NARROW_ONLY },
    ...(readOnly ? [] : [
      { separator: true as const, className: NARROW_ONLY },
      { label: 'Save as template', icon: <FileIcon size={15} />, onSelect: p.onSaveTemplate },
      ...(p.onUnpublish
        ? [{ label: 'Unpublish', icon: <EyeOffIcon size={15} />, danger: true, disabled: p.unpublishDisabled, onSelect: p.onUnpublish }]
        : []),
    ]),
  ];

  const quiet = save.kind === 'error' ? null : save.kind;
  const saveTitle = quiet === 'dirty' ? (p.onSaveNow ? 'Save now (⌘S)' : 'Publish changes to make them live') : undefined;

  return (
    <header className="pe-top">
      <div className="pe-top-left">
        <button type="button" className="pe-icon-btn pe-icon-btn--md" aria-label="Back to pages" title="Back to pages" onClick={p.onBack}>
          <ArrowLeftIcon size={16} />
        </button>
        <div className="pe-title">
          {/* The box is exactly as wide as the name (a hidden copy sizes it), so
              the status pill sits right after it. */}
          <span className="pe-title-sizer" data-value={p.name || 'Untitled page'}>
            <input className="pe-title-input" aria-label="Page name" placeholder="Untitled page" value={p.name} size={1}
              readOnly={readOnly} onChange={(e) => p.onNameChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
          </span>
          {p.unpublished
            ? <span className="pe-badge pe-badge--dot chip-amber">Unpublished changes</span>
            : <span className={`pe-badge pe-badge--dot ${p.live ? 'chip-green' : 'chip-gray'}`}>{p.live ? 'Live' : 'Draft'}</span>}
        </div>
        <button type="button" className="pe-page-switch" aria-label="Pages and sections" aria-haspopup="dialog" title={p.stepLabel}
          onClick={p.onOpenRail}>
          <LayersIcon size={14} />
          <span className="min-w-0 truncate">{p.stepLabel}</span>
          <ChevronDownIcon size={14} />
        </button>
      </div>

      <div className="pe-top-center">
        <div className="seg pe-seg pe-glide-host rounded-lg" role="group" aria-label="Preview size">
          <SegGlide active={Math.max(0, DEVICES.findIndex(([d]) => d === p.device))} />
          {DEVICES.map(([d, Icon, label]) => (
            <button key={d} type="button" data-glide-item data-device={d} aria-pressed={p.device === d} aria-label={`${label} preview`} title={label}
              className={`seg-item pe-seg-icon ${p.device === d ? 'seg-item-active' : ''}`} onClick={() => p.onDeviceChange(d)}>
              <Icon size={15} />
            </button>
          ))}
        </div>
        <div className="seg pe-seg pe-mode pe-glide-host rounded-lg" role="group" aria-label="Canvas mode">
          <SegGlide active={p.mode === 'edit' ? 0 : 1} />
          <button type="button" data-glide-item aria-pressed={p.mode === 'edit'} title="Click to select and edit"
            className={`seg-item pe-seg-text ${p.mode === 'edit' ? 'seg-item-active' : ''}`} onClick={() => p.onModeChange('edit')}>
            <PointerIcon size={14} />Edit
          </button>
          <button type="button" data-glide-item aria-pressed={p.mode === 'try'} title="Click buttons and fill forms like a visitor"
            className={`seg-item pe-seg-text ${p.mode === 'try' ? 'seg-item-active' : ''}`} onClick={() => p.onModeChange('try')}>
            <PlayIcon size={13} />Try it
          </button>
        </div>
      </div>

      <div className="pe-top-right">
        <div className="pe-top-tools">
          {!readOnly && (
            <>
              <button type="button" className="pe-icon-btn pe-icon-btn--md" aria-label="Undo" title="Undo (⌘Z)" disabled={!p.canUndo} onClick={p.onUndo}>
                <UndoIcon size={16} />
              </button>
              <button type="button" className="pe-icon-btn pe-icon-btn--md" aria-label="Redo" title="Redo (⌘⇧Z)" disabled={!p.canRedo} onClick={p.onRedo}>
                <RedoIcon size={16} />
              </button>
            </>
          )}
          <button type="button" className="pe-icon-btn pe-icon-btn--md" aria-label="Version history" title="Version history" onClick={p.onOpenHistory}>
            <HistoryIcon size={16} />
          </button>
          <button type="button" className="pe-icon-btn pe-icon-btn--md" aria-label="Analytics" title="Analytics" onClick={p.onOpenAnalytics}>
            <ChartIcon size={16} />
          </button>
          <button type="button" className="pe-icon-btn pe-icon-btn--md" aria-label="Share and embed" title="Share and embed" onClick={p.onOpenShare}>
            <ShareIcon size={16} />
          </button>
          <button type="button" className="pe-icon-btn pe-icon-btn--md" aria-label="Preview" title="Preview in a new tab"
            aria-busy={p.previewBusy || undefined} disabled={p.previewBusy} onClick={p.onPreview}>
            {p.previewBusy ? spinner : <EyeIcon size={16} />}
          </button>
        </div>

        {readOnly ? (
          <span className="pe-badge chip-gray" title="You can look around, but not change this page">View only</span>
        ) : (
          <>
            <span className="pe-save-wrap" role="status" aria-live="polite">
              {save.kind === 'error' ? (
                <button type="button" className="pe-save pe-save--error"
                  title={save.stale ? 'Someone saved this page in another tab or browser. Reload to get the latest.' : save.message}
                  onClick={save.stale ? p.onReload : save.retryable ? p.onRetry : p.onFixError}>
                  <CircleAlertIcon size={13} />
                  <span className="pe-save-text">{save.stale ? `${save.message} Reload` : save.retryable ? `${save.message} Retry` : save.message}</span>
                </button>
              ) : (
                <button type="button" className={`pe-save pe-save--${save.kind}`} disabled={!p.onSaveNow} onClick={p.onSaveNow}
                  aria-label={save.kind === 'dirty' && p.onSaveNow ? 'Unsaved changes, save now' : SAVE_TEXT[save.kind]} title={saveTitle}>
                  {SAVE_ICON[save.kind]}
                  <span className="pe-save-text">{SAVE_TEXT[save.kind]}</span>
                </button>
              )}
            </span>

            <button type="button" className="btn-primary pe-publish" disabled={p.publishDisabled} title={p.publishTitle} onClick={p.onPublish}>
              {p.publishLabel}
            </button>
          </>
        )}
        <EditorMenu label="More" title="More" items={moreItems} icon={<MoreHorizontalIcon size={16} />}
          buttonClassName="pe-icon-btn pe-icon-btn--md" width={220} />
      </div>
    </header>
  );
}
